import React, { useState, useEffect } from 'react';
import BasicDetailsForm from './BasicDetailsForm';
import PaymentGateway from './PaymentGateway';
import AdditionalDetailsForm from './AdditionalDetailsForm';
import FormSuccess from './FormSuccess';
import Alert from '../common/Alert';
import { enrollStudent, fetchEnrollmentByEmailOrId } from '../services/api';
import './EnrollmentForm.css';
import { courses } from '../Courses/courseData';

const EnrollmentForm = () => {

  const getInitialCourseDetails = () => {
    const params = new URLSearchParams(window.location.search);
    const urlCourseId = params.get('courseId');

    if (urlCourseId) {
      const matchedCourse = courses.find(c => c.courseId === urlCourseId);
      if (matchedCourse) {
        const price = matchedCourse.discountedPrice || 0;
        const priceWithGst = (price * 1.18).toFixed(2);
        // Sync to sessionStorage
        sessionStorage.setItem('courseName', matchedCourse.title);
        sessionStorage.setItem('courseFee', priceWithGst);
        sessionStorage.setItem('courseId', matchedCourse.courseId);
        return {
          courseName: matchedCourse.title,
          courseFee: parseFloat(priceWithGst, 10),
          courseId: matchedCourse.courseId
        };
      }
    }

    return {
      courseName: sessionStorage.getItem('courseName') || '',
      courseFee: parseFloat(sessionStorage.getItem('courseFee'), 10) || 0,
      courseId: sessionStorage.getItem('courseId') || ''
    };
  };

  const initialCourse = getInitialCourseDetails();

  const [currentStep, setCurrentStep] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [existingUserInput, setExistingUserInput] = useState('');
  const [formData, setFormData] = useState({
    // Basic details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    aadharNumber: '',
    address: '',
    city: '',
    district: '',
    state: '',
    pinCode: '',

    // Payment details
    paymentId: '',
    paymentStatus: '',

    // Additional details
    schoolName: '',
    schoolCity: '',
    schoolDistrict: '',
    schoolState: '',
    schoolPinCode: '',

    // Father's details
    fatherName: '',
    fatherOccupation: '',
    fatherPhone: '',
    fatherEmail: '',
    fatherAddress: '',
    fatherCity: '',
    fatherDistrict: '',
    fatherState: '',
    fatherPinCode: '',

    // Mother's details
    motherName: '',
    motherOccupation: '',
    motherPhone: '',
    motherEmail: '',
    motherAddress: '',
    motherCity: '',
    motherDistrict: '',
    motherState: '',
    motherPinCode: '',

    // Other details
    referenceSource: '',
    photo: null,
    aadharPdf: null,

    // Course details
    courseName: initialCourse.courseName,
    courseFee: initialCourse.courseFee,
    courseId: initialCourse.courseId,

    referralCode: '', // Add this line
  });

  useEffect(() => {
    let timeoutId = null;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth <= 768);
      }, 200); // 200ms debounce
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!formData.courseFee || !formData.courseName || !formData.courseId) {
      // Fallback to predefined course fees if not available in sessionStorage
      const courseFees = {
        "Organic Chemistry Mastery": 30000,
        "Inorganic Chemistry Simplified": 3999,
        "Physical Chemistry Fundamentals": 4499,
      };
      setFormData((prevData) => ({
        ...prevData,
        courseFee: courseFees[formData.courseName] || 0,
      }));
    }
  }, [formData.courseName, formData.courseFee, formData.courseId]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else if (name === 'referralCode') {
      // Convert referral code to uppercase
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const backToBasicForm = () => {
    setCurrentStep(1);
  }

  const handleCheckboxChange = (e) => {
    if (e.target.checked) {
      setFormData({
        ...formData,
        motherAddress: formData.fatherAddress,
        motherCity: formData.fatherCity,
        motherDistrict: formData.fatherDistrict,
        motherState: formData.fatherState,
        motherPinCode: formData.fatherPinCode,
      });
    }
  };

  const handleBasicDetailsSubmit = async (e) => {
    e.preventDefault();

    try {
      // Create a temporary enrollment record to get an enrollment ID
      const response = await enrollStudent({
        ...formData,
        enrollmentStatus: 'pending',
        courseName: sessionStorage.getItem('courseName'),
        courseFee: sessionStorage.getItem('courseFee'),
        courseId: sessionStorage.getItem('courseId'), // Retrieve course ID from sessionStorage
        referralCode: formData.referralCode // Add referral code
      }, false, false);

      localStorage.setItem('enrollmentId', response.enrollmentId);
      setCurrentStep(2);
    } catch (error) {
      console.error('Error submitting basic details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit basic details';
      setAlert({ type: 'error', message: errorMessage });
      throw error; // Re-throw to trigger error handling in child component
    }
  };

  const updateFormDataFromResponse = (responseData) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      firstName: responseData.first_name || '',
      lastName: responseData.last_name || '',
      email: responseData.email || '',
      confirmEmail: responseData.email || '',
      phone: responseData.phone || '',
      confirmPhone: responseData.phone || '',
      aadharNumber: responseData.aadhar_number || '',
      confirmAadhar: responseData.aadhar_number || '',
      address: responseData.address || '',
      city: responseData.city || '',
      district: responseData.district || '',
      state: responseData.state || '',
      pinCode: responseData.pin_code || '',
      schoolName: responseData.school_name || '',
      schoolCity: responseData.school_city || '',
      schoolDistrict: responseData.school_district || '',
      schoolState: responseData.school_state || '',
      schoolPinCode: responseData.school_pin_code || '',
      fatherName: responseData.father_name || '',
      fatherOccupation: responseData.father_occupation || '',
      fatherPhone: responseData.father_phone || '',
      fatherEmail: responseData.father_email || '',
      fatherAddress: responseData.father_address || '',
      fatherCity: responseData.father_city || '',
      fatherDistrict: responseData.father_district || '',
      fatherState: responseData.father_state || '',
      fatherPinCode: responseData.father_pin_code || '',
      motherName: responseData.mother_name || '',
      motherOccupation: responseData.mother_occupation || '',
      motherPhone: responseData.mother_phone || '',
      motherEmail: responseData.mother_email || '',
      motherAddress: responseData.mother_address || '',
      motherCity: responseData.mother_city || '',
      motherDistrict: responseData.mother_district || '',
      motherState: responseData.mother_state || '',
      motherPinCode: responseData.mother_pin_code || '',
      referenceSource: responseData.reference_source || '',
      photo: responseData.photo_path || null,
      aadharPdf: responseData.aadhar_pdf_path || null,
      paymentId: responseData.payment_id || '',
      paymentStatus: responseData.payment_status || '',
    }));
  };

  const handleExistingUser = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  const submitExistingUser = async () => {
    if (!existingUserInput.trim()) {
      setAlert({
        type: 'error',
        message: 'Please provide a valid Registration ID or Registered Email ID.'
      });
      return;
    }

    setShowModal(false);

    try {
      // Fetch user details based on Registration ID or Email ID
      const response = await fetchEnrollmentByEmailOrId(existingUserInput.trim(), sessionStorage.getItem('courseId'));

      localStorage.setItem('enrollmentId', response.data.enrollment_id);
      updateFormDataFromResponse(response.data);

      if (response.data.enrollment_status === 'completed') {
        setCurrentStep(4); // Move to Success step
      } else if (response.data.enrollment_status === 'payment_completed') {
        setCurrentStep(3); // Move to Additional Details step
      } else {
        setCurrentStep(2); // Move to Payment step
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch user details';
      setAlert({ type: 'error', message: errorMessage });
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    setFormData({
      ...formData,
      paymentId: paymentData.razorpay_payment_id,
      paymentStatus: 'completed',
    });
    setCurrentStep(3);
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();

    // Create FormData object for file uploads
    const submitData = new FormData();

    // Append all form data
    Object.keys(formData).forEach((key) => {
      submitData.append(key, formData[key]);
    });

    try {
      // Get enrollment ID from local storage
      const enrollmentId = localStorage.getItem('enrollmentId');
      if (!enrollmentId) {
        throw new Error('Registration ID is missing. Please restart the process.');
      }
      submitData.append('enrollmentId', enrollmentId);

      // Submit the complete form data with PUT request
      await enrollStudent(submitData, true, true);

      // Clear local storage
      // localStorage.removeItem('enrollmentId');

      // Move to success step
      setCurrentStep(4);
    } catch (error) {
      console.error('Error submitting enrollment form:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to complete enrollment';
      setAlert({ type: 'error', message: errorMessage });
      throw error; // Re-throw to trigger error handling in child component
    }
  };

  const renderProgressSteps = () => {
    if (isMobile) {
      const steps = [
        { step: 1, label: 'Basic Details' },
        { step: 2, label: 'Payment' },
        { step: 3, label: 'Additional Details' },
        { step: 4, label: 'Complete' }
      ];

      if (currentStep === 4) {
        return steps.slice(2).map(({ step, label }, index) => (
          <>{currentStep > 1 && index === 0 ? <span className='status-success'></span> : <></>}<div key={step} className='progress-step completed'>
            {label}
          </div>{currentStep < 4 && index === 1 ? <span className='status'></span> : <></>}</>
        ));
      }

      return steps.slice(currentStep - 1, currentStep + 1).map(({ step, label }, index) => (
        <>{currentStep > 1 && index === 0 ? <span className='status-success'></span> : <></>}<div key={step} className={`progress-step ${currentStep === step ? 'active' : currentStep > step ? 'completed' : ''}`}>
          {label}
        </div>{currentStep < 4 && index === 1 ? <span className='status'></span> : <></>}</>
      ));
    }

    return (
      <>
        <div className={`progress-step ${currentStep === 1 ? 'active' : currentStep > 1 ? 'completed' : ''}`}>Basic Details</div>
        <div className={`progress-step ${currentStep === 2 ? 'active' : currentStep > 2 ? 'completed' : ''}`}>Payment</div>
        <div className={`progress-step ${currentStep === 3 ? 'active' : currentStep > 3 ? 'completed' : ''}`}>Additional Details</div>
        <div className={`progress-step ${currentStep >= 4 ? 'completed' : ''}`}>Complete</div>
      </>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <BasicDetailsForm
              formData={formData}
              handleInputChange={handleInputChange}
              handleSubmit={handleBasicDetailsSubmit}
              handleExistingUser={handleExistingUser}
            />
          </div>
        );
      case 2:
        return (
          <PaymentGateway
            formData={formData}
            onPaymentSuccess={handlePaymentSuccess}
            backToBasicForm={backToBasicForm}
          />
        );
      case 3:
        return (
          <AdditionalDetailsForm
            formData={formData}
            handleInputChange={handleInputChange}
            handleCheckboxChange={handleCheckboxChange}
            handleSubmit={handleFinalSubmit}
          />
        );
      case 4:
        return <FormSuccess />;
      default:
        return <BasicDetailsForm />;
    }
  };

  return (
    <div className="enrollment-container">
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
      <img className="educatory-logo" src="/assets/BigLogo.png" alt='educatory_logo' />
      <h2 style={{ textAlign: 'center', margin: '1rem 0' }}>{formData.courseName} ({formData.courseId})</h2>
      <div className="enrollment-progress">
        {renderProgressSteps()}
      </div>

      <div className="enrollment-form">
        {renderStep()}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#0E1C48' }}>Already Registered?</h3>
            <p style={{ margin: 0, color: '#334155' }}>Enter your Registration ID or Registered Email ID:</p>
            <input 
              type="text" 
              value={existingUserInput}
              onChange={(e) => setExistingUserInput(e.target.value)}
              className="modal-input"
              placeholder="e.g., 20260620ED001 or email@example.com"
              autoFocus
            />
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
              <button type="button" onClick={submitExistingUser} className="btn btn-primary">Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrollmentForm;